import { ThemeOptions } from "@mui/material/styles";
export type AppThemeMode = "light" | "dark" | "midnight";
export const getSidebarBackground = (mode: AppThemeMode) =>
  mode === "midnight" ? "#030608" : mode === "dark" ? "#111827" : "#1e293b";

export const getDesignTokens = (mode: AppThemeMode): ThemeOptions => {
  const light = mode === "light";
  const accent = light ? "#21669C" : "#D9B76A";
  const border = light ? "#CFDFEF" : "rgba(255,255,255,0.10)";
  return {
    palette: {
      mode: light ? "light" : "dark",
      primary: { main: accent, contrastText: light ? "#FFFFFF" : "#17150F" },
      secondary: { main: light ? "#367D9C" : "#81B9AC" },
      background: {
        default: light
          ? "#EDF6FF"
          : mode === "midnight"
          ? "#090B0E"
          : "#111418",
        paper: light ? "#F9FCFF" : mode === "midnight" ? "#13161B" : "#1A1E24",
      },
      text: {
        primary: light ? "#20364D" : "#F3F4F6",
        secondary: light ? "#516B82" : "#AAB2BE",
      },
      divider: border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily:
        '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 800 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700, letterSpacing: "-0.025em" },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.6 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            scrollbarColor: light ? "#AEC9E2 #EDF6FF" : "#424952 #111418",
          },
          "*:focus-visible": {
            outline: "2px solid " + accent,
            outlineOffset: 3,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: "10px 20px",
            minHeight: 44,
            transition: "background-color 160ms, border-color 160ms",
            fontWeight: 600,
          },
          sizeSmall: { minHeight: 36, padding: "6px 14px" },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { minWidth: 44, minHeight: 44 } },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          elevation1: {
            boxShadow: light
              ? "0 4px 20px rgba(39,87,130,0.08)"
              : "0 8px 28px rgba(0,0,0,0.18)",
          },
        },
      },
      MuiCard: { styleOverrides: { root: { border: "1px solid " + border } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: light ? "#FFFFFF" : "rgba(0,0,0,0.10)",
          },
          notchedOutline: { borderColor: border },
          input: { paddingTop: 14, paddingBottom: 14 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            "&.Mui-selected": {
              backgroundColor: light ? "#DCEEFF" : "rgba(217,183,106,0.14)",
              color: accent,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            color: light ? "#516B82" : "#AAB2BE",
            backgroundColor: light ? "#E7F2FD" : "#15191E",
          },
          root: { borderColor: border, padding: "16px" },
        },
      },
      MuiTab: {
        styleOverrides: { root: { minHeight: 48, textTransform: "none" } },
      },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, alignItems: "center" } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { border: "1px solid " + border },
          paperFullScreen: {
            paddingTop: "env(safe-area-inset-top)",
            boxSizing: "border-box",
          },
        },
      },
    },
  };
};
